import { useId, useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';
import { t } from '../../lib/i18n';
import { Button } from '../ui';

export interface UploadLimits {
  /** Largest file in bytes (0 or omitted = no limit). */
  maxBytes?: number;
  /** Allowed MIME types (`video/*` style wildcards allowed) or extensions (`.pdf`). Empty = any. */
  accept?: readonly string[];
}

function formatMb(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return mb >= 10 ? String(Math.round(mb)) : (Math.round(mb * 10) / 10).toString();
}

/** Why a file can't be uploaded, as a translated sentence, or null when it is fine. */
export function uploadProblem(file: { name: string; size: number; type: string }, limits: UploadLimits): string | null {
  if (limits.maxBytes && file.size > limits.maxBytes) {
    return t('p3w.upload.tooBig', { name: file.name, size: formatMb(file.size), max: formatMb(limits.maxBytes) });
  }
  const accept = limits.accept ?? [];
  if (accept.length > 0) {
    const lower = file.name.toLowerCase();
    const type = file.type.toLowerCase();
    const ok = accept.some((a) => {
      const rule = a.toLowerCase();
      if (rule.startsWith('.')) return lower.endsWith(rule);
      if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
      return type === rule;
    });
    if (!ok) return t('p3w.upload.badType', { name: file.name });
  }
  return null;
}

export interface UploadHandle {
  promise: Promise<unknown>;
  abort: () => void;
}

/** Sends one file as multipart with XHR so progress is reported. Resolves with the parsed JSON body (or null). */
export function startUpload(
  file: File,
  opts: { url: string; method?: string; field?: string; fields?: Record<string, string>; onProgress?: (fraction: number) => void },
): UploadHandle {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<unknown>((resolve, reject) => {
    xhr.open(opts.method ?? 'POST', opts.url);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) opts.onProgress?.(Math.min(1, e.loaded / e.total));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(xhr.responseText ? JSON.parse(xhr.responseText) : null);
        } catch {
          resolve(null);
        }
      } else {
        reject(new Error(t('p3w.upload.failed')));
      }
    };
    xhr.onerror = () => reject(new Error(t('p3w.upload.failed')));
    xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));
    const body = new FormData();
    for (const [k, v] of Object.entries(opts.fields ?? {})) body.append(k, v);
    body.append(opts.field ?? 'file', file);
    xhr.send(body);
  });
  return { promise, abort: () => xhr.abort() };
}

export interface UploadWithProgressProps extends UploadLimits {
  /** Visible label for the group. */
  label: string;
  url: string;
  method?: string;
  field?: string;
  fields?: Record<string, string>;
  hint?: string;
  disabled?: boolean;
  onDone: (response: unknown, file: File) => void;
  onError?: (message: string) => void;
}

/** Choose a file, check size and type, upload with a progress bar and a Cancel button. */
export function UploadWithProgress({ label, url, method, field, fields, hint, disabled, maxBytes, accept, onDone, onError }: UploadWithProgressProps) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const handle = useRef<UploadHandle | null>(null);
  const [name, setName] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const fail = (message: string) => {
    setError(message);
    onError?.(message);
  };

  const pick = (file: File | undefined) => {
    if (!file) return;
    setError('');
    setDone(false);
    const problem = uploadProblem(file, { maxBytes, accept });
    if (problem) {
      fail(problem);
      return;
    }
    setName(file.name);
    setProgress(0);
    const h = startUpload(file, { url, method, field, fields, onProgress: setProgress });
    handle.current = h;
    h.promise
      .then((res) => {
        setProgress(null);
        setDone(true);
        onDone(res, file);
      })
      .catch((e: unknown) => {
        setProgress(null);
        if (e instanceof DOMException && e.name === 'AbortError') setError(t('p3w.upload.cancelled'));
        else fail(e instanceof Error ? e.message : t('p3w.upload.failed'));
      })
      .finally(() => {
        handle.current = null;
        if (input.current) input.current.value = '';
      });
  };

  const uploading = progress !== null;
  const percent = Math.round((progress ?? 0) * 100);
  return (
    <div role="group" aria-labelledby={`${id}-l`} className="space-y-2">
      <p id={`${id}-l`} className="text-sm font-medium text-slate-800">
        {label}
      </p>
      <input
        ref={input}
        id={`${id}-f`}
        type="file"
        className="sr-only peer"
        accept={accept?.join(',')}
        disabled={disabled || uploading}
        onChange={(e) => pick(e.target.files?.[0])}
        aria-describedby={hint ? `${id}-h` : undefined}
      />
      {!uploading ? (
        <label
          htmlFor={`${id}-f`}
          className="inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-md border border-slate-300 bg-white px-3.5 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50 peer-focus-visible:ring-2"
        >
          <Upload size={16} aria-hidden="true" />
          {t('p3w.upload.choose')}
        </label>
      ) : (
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <progress className="h-2 flex-1" max={100} value={percent} aria-label={t('p3w.upload.progress', { name })} />
            <span className="w-10 text-right text-sm tabular-nums">{percent}%</span>
            <Button onClick={() => handle.current?.abort()}>
              <X size={16} aria-hidden="true" />
              {t('common.cancel')}
            </Button>
          </div>
          <p className="text-xs text-slate-600">{name}</p>
        </div>
      )}
      {hint ? (
        <p id={`${id}-h`} className="text-xs text-slate-600">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      ) : null}
      {done ? (
        <p role="status" className="text-sm text-green-800">
          {t('p3w.upload.done', { name })}
        </p>
      ) : null}
    </div>
  );
}
